import { useEffect } from "react";
import { LanderConfigAction, useLanderConfig } from "../commons/ConfigContext";
import { System } from "../commons/Constants";
import { ExperimentIds } from "../commons/constants/experiment";
import { BlendConfig } from "../yahoo/types";
import { isControlCohort } from "./helper";

/** Rows rendered when a cohort does not say otherwise. */
const DEFAULT_TOTAL_ROWS = 6;

/** Namespace for the Yahoo `type=` tag this arm reports under. */
const TYPE_TAG_PREFIX = "PW_YAHOO_BLEND_";

/**
 * Longest cohort name encoded into a tag. Cohort names come from Switchboard, so this
 * guards against an absurd value reaching Yahoo's `type=` param — real cohorts are a
 * dozen characters and will never be truncated.
 */
const MAX_COHORT_TAG_LENGTH = 24;

/**
 * Cohort config is published through Switchboard, which does not type its values, so a
 * count entered as "3" arrives as a string and a flag entered as "false" arrives as a
 * truthy string. Both coercions below exist for that reason — never assert the type.
 */
function toCount(value: unknown, fallback: number): number {
  const parsed =
    typeof value === "number" ? value : parseInt(String(value ?? ""), 10);

  return Number.isFinite(parsed) && parsed >= 0 ? Math.floor(parsed) : fallback;
}

/** Absent means `true`, so a cohort can publish no `interleave` key at all. */
function toInterleave(value: unknown): boolean {
  return value !== false && String(value).toLowerCase() !== "false";
}

/**
 * The Yahoo `type=` tag for one cohort of this arm.
 *
 * Per cohort rather than per arm: cohorts here differ only in their blend ratio, and
 * folding them into one TREATMENT bucket would hide the very comparison the runtime
 * ratio exists to make. Control is tagged too — without a tag on both sides Yahoo's
 * own RPM reporting cannot be split.
 *
 * Yahoo accepts only alphanumerics and underscores in `type=`, so anything else folds
 * to an underscore. Yields no tag at all when nothing usable survives, rather than a
 * bare prefix.
 */
export function blendTypeTag(cohort: string): string | undefined {
  const slug = cohort
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, MAX_COHORT_TAG_LENGTH)
    .replace(/_+$/g, "");

  if (!slug) return;

  return `${TYPE_TAG_PREFIX}${slug}`;
}

/**
 * Blended-keywords experiment (ParkWeb, PARKING-5269).
 *
 * The treatment arms render our high-value `optimizedKeywords` and Yahoo's own related
 * terms in one block, with the split and ordering coming from Hivemind cohort config on
 * `experiment.data`. Control is excluded from `inBlendTreatment`, so it takes the
 * pre-blend path and renders Yahoo's terms only.
 *
 * Deliberately independent of `useOptimizedKeywordsExperiment` (PARKING-5211): each hook
 * gates on its own experiment id, and `resolvePartnerTerms` gives each arm its own branch
 * so retiring one does not disturb the other.
 *
 * Also owns the arm's `yahoo.typeTag`. The returned `yahooTypeTag` is resolved during
 * render and is what the RS fetch must use: reading the dispatched value instead would
 * mean fetching once untagged and again once the dispatch lands, doubling this arm's RS
 * request volume to Yahoo. The dispatch exists for the readers that cannot be handed the
 * value directly — row hrefs and the search box.
 */
export function useBlendedKeywordsExperiment() {
  const { landerConfig, landerConfigDispatch } = useLanderConfig();
  const { key, cohort, data } = landerConfig.experiment || {};

  const inExperiment = key === ExperimentIds.yahooBlendedKeywords;
  const isParkWeb = landerConfig.system === System.PARKWEB;
  const inBlendTreatment =
    inExperiment && isParkWeb && !!cohort && !isControlCohort(cohort);

  const ownsTypeTag = inExperiment && isParkWeb && !!cohort;
  // An empty cohort yields no tag, which is exactly what "this arm does not apply" needs.
  const ownTypeTag = blendTypeTag(ownsTypeTag ? cohort : "");
  const stampedTypeTag: string | undefined = landerConfig.yahoo?.typeTag;
  // This experiment owns per-cohort reporting, including traffic the API routes
  // to Yahoo under a shared fallback tag such as pwLowRelevancyRedirect.
  const yahooTypeTag = ownTypeTag ?? stampedTypeTag;

  useEffect(() => {
    if (!ownTypeTag || stampedTypeTag === ownTypeTag) return;

    landerConfigDispatch({
      type: LanderConfigAction.UPDATE_YAHOO_TYPE_TAG,
      payload: { typeTag: ownTypeTag },
    });
  }, [ownTypeTag, stampedTypeTag, landerConfigDispatch]);

  const blendConfig: BlendConfig = {
    partnerTermCount: toCount(data?.partnerTermCount, 0),
    // A cohort configuring zero rows would render an empty block, which is never the
    // intent; treat it as unset.
    totalRows:
      toCount(data?.totalRows, DEFAULT_TOTAL_ROWS) || DEFAULT_TOTAL_ROWS,
    interleave: toInterleave(data?.interleave),
  };

  return { inBlendTreatment, inExperiment, blendConfig, yahooTypeTag };
}
